import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectFilteredDiamonds } from '../../redux/slices/selectionSlice';

const fmt$ = v => '$' + Math.round(v).toLocaleString('en-US');
const fmtR = v => isNaN(v) ? '—' : v.toFixed(1) + '%';
const fmtCt = v => v.toFixed(2);

function aggregate(list) {
  const count      = list.length;
  const totalCt    = list.reduce((s, d) => s + parseFloat(d.Carats       || 0), 0);
  const totalValue = list.reduce((s, d) => s + parseFloat(d['Total Price']|| 0), 0);
  const avgRap     = count > 0
    ? list.reduce((s, d) => s + parseFloat(d['Rap %'] || 0), 0) / count
    : 0;
  return { count, totalCt, totalValue, avgRap };
}

const SelectionBreakdown = () => {
  const diamonds = useSelector(selectFilteredDiamonds);
  const [groupBy, setGroupBy] = useState('demand');
  const [sortBy, setSortBy] = useState('count');
  const [sortOrder, setSortOrder] = useState('desc');

  const primaryField   = groupBy === 'demand' ? 'Demand Name' : 'Supplier';
  const secondaryField = groupBy === 'demand' ? 'Supplier'    : 'Demand Name';

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const sortAggregates = (list) => {
    return [...list].sort((a, b) => {
      let aVal, bVal;
      if (sortBy === 'count') {
        aVal = a.count;
        bVal = b.count;
      } else if (sortBy === 'totalCt') {
        aVal = a.totalCt;
        bVal = b.totalCt;
      } else if (sortBy === 'totalValue') {
        aVal = a.totalValue;
        bVal = b.totalValue;
      } else if (sortBy === 'avgRap') {
        aVal = a.avgRap;
        bVal = b.avgRap;
      }
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  const SortIndicator = ({ field }) => {
    if (sortBy !== field) return null;
    return <span style={{ marginLeft: 4 }}>{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  };

  const grouped = useMemo(() => {
    const map = {};
    diamonds.forEach(d => {
      const pk = d[primaryField]   || '(none)';
      const sk = d[secondaryField] || '(none)';
      if (!map[pk]) map[pk] = {};
      if (!map[pk][sk]) map[pk][sk] = [];
      map[pk][sk].push(d);
    });
    return map;
  }, [diamonds, primaryField, secondaryField]);

  const primaryKeys = useMemo(() => {
    const keys = Object.keys(grouped);
    const aggregates = keys.map(pk => ({
      key: pk,
      ...aggregate(Object.values(grouped[pk]).flat())
    }));
    const sorted = sortAggregates(aggregates);
    return sorted.map(item => item.key);
  }, [grouped, sortBy, sortOrder]);

  const grandAgg    = aggregate(diamonds);

  return (
    <div>
      {/* Group-by toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Group by:</span>
        <button
          className={`btn btn-sm ${groupBy === 'demand' ? 'btn-primary' : 'btn-outline-secondary'}`}
          style={{ fontSize: 12, padding: '2px 12px' }}
          onClick={() => setGroupBy('demand')}
        >
          Demand
        </button>
        <button
          className={`btn btn-sm ${groupBy === 'supplier' ? 'btn-primary' : 'btn-outline-secondary'}`}
          style={{ fontSize: 12, padding: '2px 12px' }}
          onClick={() => setGroupBy('supplier')}
        >
          Supplier
        </button>
      </div>

      {/* Breakdown table */}
      <div style={{ overflowX: 'auto' }}>
        <table className="table table-sm table-bordered" style={{ fontSize: 12, marginBottom: 0 }}>
          <thead className="thead-light">
            <tr>
              <th style={{ minWidth: 180 }}>{groupBy === 'demand' ? 'Demand' : 'Supplier'}</th>
              <th style={{ minWidth: 140 }}>{groupBy === 'demand' ? 'Supplier' : 'Demand'}</th>
              <th style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('count')}>
                # <SortIndicator field="count" />
              </th>
              <th style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('totalCt')}>
                Total Ct <SortIndicator field="totalCt" />
              </th>
              <th style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('totalValue')}>
                Total Value <SortIndicator field="totalValue" />
              </th>
              <th style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('avgRap')}>
                Avg Rap% <SortIndicator field="avgRap" />
              </th>
            </tr>
          </thead>
          <tbody>
            {primaryKeys.map(pk => {
              const secondaryMap  = grouped[pk];
              const allStones     = Object.values(secondaryMap).flat();
              const tot           = aggregate(allStones);
              
              const secondaryAggregates = Object.keys(secondaryMap).map(sk => ({
                key: sk,
                ...aggregate(secondaryMap[sk])
              }));
              const sortedSecondary = sortAggregates(secondaryAggregates);
              const secondaryKeys = sortedSecondary.map(item => item.key);

              return (
                <React.Fragment key={pk}>
                  <tr style={{ background: '#f0f4ff', fontWeight: 600 }}>
                    <td colSpan={2}>{pk}</td>
                    <td style={{ textAlign: 'right' }}>{tot.count}</td>
                    <td style={{ textAlign: 'right' }}>{fmtCt(tot.totalCt)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt$(tot.totalValue)}</td>
                    <td style={{ textAlign: 'right' }}>{fmtR(tot.avgRap)}</td>
                  </tr>
                  {secondaryKeys.map(sk => {
                    const a = aggregate(secondaryMap[sk]);
                    return (
                      <tr key={sk} style={{ fontSize: 11 }}>
                        <td style={{ color: '#ccc' }}></td>
                        <td style={{ paddingLeft: 16, color: '#555' }}>{sk}</td>
                        <td style={{ textAlign: 'right' }}>{a.count}</td>
                        <td style={{ textAlign: 'right' }}>{fmtCt(a.totalCt)}</td>
                        <td style={{ textAlign: 'right' }}>{fmt$(a.totalValue)}</td>
                        <td style={{ textAlign: 'right' }}>{fmtR(a.avgRap)}</td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 700, background: '#e2e8f0' }}>
              <td colSpan={2}>Total</td>
              <td style={{ textAlign: 'right' }}>{grandAgg.count}</td>
              <td style={{ textAlign: 'right' }}>{fmtCt(grandAgg.totalCt)}</td>
              <td style={{ textAlign: 'right' }}>{fmt$(grandAgg.totalValue)}</td>
              <td style={{ textAlign: 'right' }}>{fmtR(grandAgg.avgRap)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default SelectionBreakdown;
